import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { focusGrid } from '@/app/helpers/focusGrid';
import type { JsPivotFieldButton, JsRenderCodeCell } from '@/app/quadratic-core-types';
import { atom, getDefaultStore } from 'jotai';
import type { Point } from 'pixi.js';

export enum ContextMenuType {
  Grid = 'grid',
  Table = 'table',
  TableSort = 'table-sort',
  TableColumn = 'table-column',
  TableColumnFilter = 'table-column-filter',
  SheetColumnFilter = 'sheet-column-filter',
  Chart = 'chart',
  PivotField = 'pivot-field',
}

export interface ContextMenuState {
  type?: ContextMenuType;
  world?: Point;
  column?: number;
  row?: number;
  table?: JsRenderCodeCell;
  rename?: boolean;
  selectedColumn?: number;
  initialValue?: string;
  /** The field header button that opened a PivotField menu. */
  pivotField?: JsPivotFieldButton;
}

export const defaultContextMenuState: ContextMenuState = {
  type: undefined,
  world: undefined,
  column: undefined,
  row: undefined,
  table: undefined,
  rename: undefined,
  selectedColumn: undefined,
  initialValue: undefined,
  pivotField: undefined,
};

export const contextMenuAtom = atom<ContextMenuState>(defaultContextMenuState);

let contextMenuListenersInitialized = false;

export const initContextMenuListeners = () => {
  if (contextMenuListenersInitialized) return () => {};
  contextMenuListenersInitialized = true;

  const store = getDefaultStore();

  // The selection at the time the open menu was set, used to tell real cursor
  // navigation apart from core re-sending an unchanged selection.
  let openSelection: string | undefined;

  // Closes the open context menu and returns focus to the grid. If no menu is
  // open this must be a no-op: cursorPosition fires for reasons unrelated to
  // user navigation (e.g. core re-sending the A1 context after a transaction),
  // and stealing focus then would yank the user out of panel inputs like the
  // chart editor.
  const clear = () => {
    if (store.get(contextMenuAtom).type === undefined) return;
    store.set(contextMenuAtom, {});
    focusGrid();
  };

  // Only close the menu when the selection actually changed. Transactions
  // applied while a menu stays open (e.g. checking items in the filter menu)
  // make core re-send the cursor without any navigation having happened.
  const onCursorPosition = () => {
    if (store.get(contextMenuAtom).type === undefined) return;
    if (sheets.sheet.cursor.save() === openSelection) return;
    clear();
  };

  const set = (options: ContextMenuState) => {
    if (!options.type) {
      clear();
    } else {
      openSelection = sheets.sheet.cursor.save();
      store.set(contextMenuAtom, options);
    }
  };

  events.on('cursorPosition', onCursorPosition);
  events.on('contextMenu', set);

  return () => {
    events.off('cursorPosition', onCursorPosition);
    events.off('contextMenu', set);
    contextMenuListenersInitialized = false;
  };
};
